import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

const WINDOWS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Last 7 days' },
  { value: 'month', label: 'Last 30 days' },
];

// Mirrors backend services/usage._format_cost: keeps scannable digit
// counts (3 decimals at >= 1c, 4 decimals sub-cent, $0.0000 for zero).
function formatCost(usd, inr) {
  if (usd >= 0.01) return `$${usd.toFixed(3)} (≈ ₹${inr.toFixed(2)})`;
  if (usd > 0) return `$${usd.toFixed(4)} (≈ ₹${inr.toFixed(4)})`;
  return '$0.0000';
}

function formatTokens(n) {
  return Number(n || 0).toLocaleString();
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function AdminUsage() {
  // null = status check pending; false = not signed in / not admin
  // (handled by Navigate); object = signed-in user details.
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [window, setWindow] = useState('month');

  // Step 1 — confirm signed in AND admin. The is_admin field on
  // /user/status is the UX gate; the actual security gate is server-side
  // require_admin on /admin/usage. A non-admin who hits /admin directly
  // gets redirected here, then bounced to "/" by the Navigate below.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/web/user/status`);
        if (!cancelled) setUser(r.data);
      } catch {
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setAuthChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchStats = useCallback(async (win) => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/web/admin/usage`, { params: { window: win } });
      setStats(r.data);
    } catch (e) {
      // 403 here means the cookie expired or the email left the
      // allowlist between auth check and this fetch — bounce back.
      if (e?.response?.status === 403) {
        setUser(false);
      } else if (e?.response?.status === 503) {
        setError('Telemetry temporarily unavailable. Try again in a moment.');
      } else {
        setError('Failed to load admin usage. Check the network tab.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Step 2 — once we know they're admin, fetch the data.
  useEffect(() => {
    if (user && user.is_admin) {
      fetchStats(window);
    }
  }, [user, window, fetchStats]);

  if (authChecking) {
    return <div style={styles.center}>Checking authorization…</div>;
  }
  if (!user || user.is_admin === false || user === false) {
    return <Navigate to="/" replace />;
  }

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.h1}>Admin · Usage</h1>
          <div style={styles.subtitle}>
            Signed in as <strong>{user.email}</strong>
          </div>
        </div>
        <a href="/" style={styles.backLink}>← Back to dashboard</a>
      </header>

      <div style={styles.windowBar}>
        {WINDOWS.map(w => (
          <button
            key={w.value}
            onClick={() => setWindow(w.value)}
            style={{
              ...styles.windowBtn,
              ...(window === w.value ? styles.windowBtnActive : {}),
            }}
          >
            {w.label}
          </button>
        ))}
      </div>

      {loading && <div style={styles.loading}>Loading…</div>}
      {error && <div style={styles.error}>{error}</div>}

      {stats && !loading && (
        <>
          <section style={styles.totalCard}>
            <div style={styles.totalCost}>
              {formatCost(stats.total.cost_usd, stats.total.cost_inr)}
            </div>
            <div style={styles.totalMeta}>
              <span>{stats.total.unique_users.toLocaleString()} users</span>
              <span>·</span>
              <span>{stats.total.calls.toLocaleString()} API calls</span>
              <span>·</span>
              <span>{formatTokens(stats.total.total_tokens)} tokens</span>
            </div>
            <div style={styles.windowLabel}>
              window: {stats.window} · since {new Date(stats.since).toLocaleDateString()}
            </div>
          </section>

          {stats.system_events && stats.system_events.calls > 0 && (
            <div style={styles.note}>
              Total includes <strong>{stats.system_events.calls.toLocaleString()}</strong>{' '}
              system-level event{stats.system_events.calls === 1 ? '' : 's'}{' '}
              ({formatCost(stats.system_events.cost_usd, stats.system_events.cost_usd * 83)})
              not shown in the per-user breakdown.
            </div>
          )}

          <section style={styles.tableWrap}>
            <h2 style={styles.h2}>Per-user breakdown</h2>
            {stats.users.length === 0 ? (
              <div style={styles.empty}>No human-user activity in this window.</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Kind</th>
                    <th style={styles.th}>User</th>
                    <th style={styles.thNum}>Calls</th>
                    <th style={styles.thNum}>Tokens</th>
                    <th style={styles.thNum}>Cost (USD)</th>
                    <th style={styles.thNum}>Cost (INR)</th>
                    <th style={styles.th}>Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.users.map(u => (
                    <tr key={`${u.user_kind}:${u.user_id}`}>
                      <td style={styles.td}>{u.user_kind}</td>
                      <td style={styles.td}>{u.user_id}</td>
                      <td style={styles.tdNum}>{u.calls.toLocaleString()}</td>
                      <td style={styles.tdNum}>{formatTokens(u.total_tokens)}</td>
                      <td style={styles.tdNum}>${u.cost_usd.toFixed(4)}</td>
                      <td style={styles.tdNum}>₹{u.cost_inr.toFixed(2)}</td>
                      <td style={styles.td}>{formatDateTime(u.last_active)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}

const styles = {
  page: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '24px 20px 60px',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  h1: { margin: 0, fontSize: 28 },
  subtitle: { color: '#666', fontSize: 14, marginTop: 4 },
  backLink: { color: '#0066cc', textDecoration: 'none', fontSize: 14 },
  windowBar: {
    display: 'flex', gap: 8, marginBottom: 20,
  },
  windowBtn: {
    padding: '8px 16px',
    border: '1px solid #ddd',
    background: '#fff',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14,
  },
  windowBtnActive: {
    background: '#0066cc',
    color: '#fff',
    borderColor: '#0066cc',
  },
  loading: { padding: 40, textAlign: 'center', color: '#666' },
  error: {
    padding: 16,
    background: '#fff5f5',
    border: '1px solid #fcc',
    borderRadius: 6,
    color: '#c33',
    marginBottom: 16,
  },
  totalCard: {
    background: '#f8f9fa',
    border: '1px solid #e3e6ea',
    borderRadius: 8,
    padding: 24,
    marginBottom: 12,
  },
  totalCost: { fontSize: 32, fontWeight: 700 },
  totalMeta: {
    display: 'flex', gap: 8, color: '#555', fontSize: 14, marginTop: 8,
  },
  windowLabel: { color: '#888', fontSize: 12, marginTop: 8 },
  note: {
    padding: '10px 14px',
    background: '#fffbe6',
    border: '1px solid #ffe58f',
    borderRadius: 6,
    fontSize: 13,
    marginBottom: 20,
  },
  tableWrap: { marginTop: 16 },
  h2: { fontSize: 18, marginBottom: 12 },
  empty: { color: '#777', fontStyle: 'italic', padding: 16 },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    textAlign: 'left',
    padding: '8px 10px',
    borderBottom: '2px solid #e3e6ea',
    fontWeight: 600,
    color: '#444',
  },
  thNum: {
    textAlign: 'right',
    padding: '8px 10px',
    borderBottom: '2px solid #e3e6ea',
    fontWeight: 600,
    color: '#444',
  },
  td: { padding: '8px 10px', borderBottom: '1px solid #eee' },
  tdNum: {
    padding: '8px 10px',
    borderBottom: '1px solid #eee',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  center: { textAlign: 'center', padding: 60, color: '#666' },
};

export default AdminUsage;
